import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, PauseIcon, PlayIcon, StarIcon } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { business } from "../data/business";
import { reviews } from "../data/reviews";
import { ui } from "../data/ui";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { SectionHeading } from "./SectionHeading";
export function Testimonials() {
  const {
    t
  } = useLanguage();
  const reduce = useReducedMotion();
  const isLg = useMediaQuery("(min-width: 1024px)");
  const isMd = useMediaQuery("(min-width: 768px)");
  const perView = isLg ? 3 : isMd ? 2 : 1;
  const maxIndex = Math.max(0, reviews.length - perView);
  const [index, setIndex] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const rotating = !reduce && !userPaused && !hovering;
  useEffect(() => {
    setIndex(i => Math.min(i, maxIndex));
  }, [maxIndex]);
  useEffect(() => {
    if (!rotating) return;
    const id = window.setInterval(() => setIndex(i => i >= maxIndex ? 0 : i + 1), 6000);
    return () => window.clearInterval(id);
  }, [rotating, maxIndex]);
  const go = (next: number) => setIndex(Math.max(0, Math.min(maxIndex, next)));
  const ratingAside = <a href={business.googleReviewsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 self-start border-l-2 border-gold pl-5 lg:self-auto" data-id="element-362" data-mp="zvwhix0bra8b">
      <span className="font-display text-5xl font-bold text-gold" data-id="element-363" data-mp="q7648ozu3zu0">{business.rating}</span>
      <span data-id="element-364" data-mp="v5g0l2xap6h7">
        <span className="flex gap-0.5" aria-label="5 out of 5 stars" data-id="element-365" data-mp="v57qfisk4f9p">
          {Array.from({
          length: 5
        }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-gold text-gold" aria-hidden="true" data-id="element-366" data-mp="1iuih27g70ta4" data-mpi={i} />)}
        </span>
        <span className="mt-1 block text-sm font-semibold text-white" data-id="element-367" data-mp="1nnbk8qw0jtk3">
          {business.reviewCount}+ {t(ui.hero.reviews)}
        </span>
        <span className="block text-xs text-smoke" data-id="element-368" data-mp="1sj5wbl1a913g">{t(ui.reviews.trusted)}</span>
      </span>
    </a>;
  return <section id="reviews" aria-labelledby="reviews-title" className="bg-charcoal py-20 lg:py-28" data-id="element-369" data-mp="1ogjca6auskgt">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-370" data-mp="1jj0zu5xmnn42">
        <SectionHeading id="reviews-title" title={t(ui.reviews.title)} subtitle={t(ui.reviews.subtitle)} aside={ratingAside} data-id="element-371" data-mp="1jkkghxn7406d" />

        <div className="mt-12" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} onFocus={() => setHovering(true)} onBlur={() => setHovering(false)} data-id="element-372" data-mp="19soe4ae2tnwc">
          <div className="overflow-hidden" data-id="element-373" data-mp="10t1he1wal69m">
            <motion.ul className="-mx-2.5 flex" aria-live={rotating ? "off" : "polite"} animate={{
            x: `-${index * (100 / perView)}%`
          }} transition={{
            duration: reduce ? 0 : 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-374" data-mp="vsnf70hdgqji">
              {reviews.map((review, i) => {
              const visible = i >= index && i < index + perView;
              return <li key={review.name} className="px-2.5" style={{
                flex: `0 0 ${100 / perView}%`
              }} aria-hidden={!visible} data-id="element-375" data-mp="3cek3jiivq80x" data-mpi={i}>
                    <figure className="flex h-full flex-col border border-gold/25 bg-ink p-7" data-id="element-376" data-mp="3gfdqa8kl5gs8">
                      <div className="flex items-center justify-between gap-3" data-id="element-377" data-mp="2jrh66tpxw3c9">
                        <span className="flex gap-0.5" aria-label="5 out of 5 stars" data-id="element-378" data-mp="2oozeqj01ogjq">
                          {Array.from({
                        length: 5
                      }).map((_, s) => <StarIcon key={s} className="h-4 w-4 fill-gold text-gold" aria-hidden="true" data-id="element-379" data-mp="2sozymj5wxjy6" data-mpi={s} />)}
                        </span>
                        <span className="rounded-sm bg-gold/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-gold" data-id="element-380" data-mp="20z584j6bjo7x">
                          {t(review.service)}
                        </span>
                      </div>
                      <blockquote className="mt-5 font-display text-xl leading-snug text-white" data-id="element-381" data-mp="1luk7hnkweq83">“{t(review.quote)}”</blockquote>
                      <p className="mt-4 text-sm italic text-gold" data-id="element-382" data-mp="1vn87er76dab1">{t(review.tag)}</p>
                      <figcaption className="mt-auto border-t border-white/10 pt-5" data-id="element-383" data-mp="1qw9n7gj2fs2i">
                        <span className="block text-sm font-semibold text-white" data-id="element-384" data-mp="24neenvca5e1r">{review.name}</span>
                        <span className="block text-xs text-smoke" data-id="element-385" data-mp="1zoblb85nww9m">{t(review.company)}</span>
                      </figcaption>
                    </figure>
                  </li>;
            })}
            </motion.ul>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4" data-id="element-386" data-mp="29nspfl59n23h">
            <div className="flex items-center gap-2" data-id="element-387" data-mp="k6rnolol87id">
              <button type="button" onClick={() => go(index - 1)} disabled={index === 0} aria-label={t(ui.reviews.prev)} className="grid h-11 w-11 place-items-center rounded-sm border border-white/20 text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-white" data-id="element-388" data-mp="k9e3t9pjnvc7">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-389" data-mp="2h578fkjqegh2" />
              </button>
              <button type="button" onClick={() => go(index + 1)} disabled={index === maxIndex} aria-label={t(ui.reviews.next)} className="grid h-11 w-11 place-items-center rounded-sm border border-white/20 text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-white" data-id="element-390" data-mp="2m3sazqmghc8o">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-391" data-mp="235pkt8i6999a" />
              </button>
              {!reduce && <button type="button" onClick={() => setUserPaused(p => !p)} aria-label={userPaused ? t(ui.reviews.play) : t(ui.reviews.pause)} className="grid h-11 w-11 place-items-center rounded-sm text-white/70 transition-colors duration-150 ease-out hover:text-gold" data-id="element-392" data-mp="3smgt2aeljejy">
                  {userPaused ? <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-393" data-mp="3r2yn3sr3vxc3" /> : <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-394" data-mp="3m4x6deh5ka3s" />}
                </button>}
              <div className="ml-2 flex gap-1.5" data-id="element-395" data-mp="3h62d1ilk735f">
                {Array.from({
                length: maxIndex + 1
              }).map((_, i) => <button key={i} type="button" onClick={() => go(i)} aria-label={`${t(ui.reviews.goTo)} ${i + 1}`} aria-current={i === index} className="grid h-6 w-6 place-items-center" data-id="element-396" data-mp="iogcbj9s63os" data-mpi={i}>
                    <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? "w-5 bg-gold" : "w-1.5 bg-white/30"}`} data-id="element-397" data-mp="3yr6vbunndip" />
                  </button>)}
              </div>
            </div>
            <a href={business.googleReviewsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-gold" data-id="element-398" data-mp="14x3uaie1gfs6">
              <span className="link-slide" data-id="element-399" data-mp="1909zdmpf00x2">{t(ui.reviews.readMore)}</span>
              <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-400" data-mp="1dwii1a2tmc8z" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}